import { ArrowUpRight, Facebook, Instagram, Mail, MapPin, Phone, Twitter, Youtube } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { contact, content } from "@/i18n/content";
import { Reveal, SectionHeading } from "@/components/Reveal";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Twitter, label: "X (Twitter)", href: "https://x.com" },
  { icon: Youtube, label: "YouTube", href: "https://youtube.com" },
];

const Contact = () => {
  const { pick, lang } = useLanguage();
  const waText = encodeURIComponent(
    lang === "hi"
      ? "नमस्ते, मैं संजय शर्मा जी के सामुदायिक कार्यों में स्वयंसेवक बनना चाहता/चाहती हूं।"
      : "Hello, I would like to volunteer with Sanjay Sharma's community initiatives."
  );

  const channels = [
    {
      key: "phone",
      icon: Phone,
      value: contact.phoneDisplay,
      href: contact.phoneHref,
      testid: "contact-phone-link",
    },
    {
      key: "whatsapp",
      icon: WhatsAppIcon,
      value: "WhatsApp Chat",
      href: `${contact.whatsapp}?text=${waText}`,
      testid: "contact-whatsapp-link",
    },
    {
      key: "email",
      icon: Mail,
      value: contact.email,
      href: `mailto:${contact.email}?subject=${encodeURIComponent("General Enquiry")}`,
      testid: "contact-email-link",
    },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#151515] text-[#F7F5F0] py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24">
        <SectionHeading
          dark
          eyebrow={pick(content.contact.eyebrow)}
          heading={pick(content.contact.heading)}
          sub={pick(content.contact.sub)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-8">
            {channels.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08} y={30}>
                <a
                  href={c.href}
                  data-testid={c.testid}
                  target={c.key === "whatsapp" ? "_blank" : undefined}
                  rel={c.key === "whatsapp" ? "noopener noreferrer" : undefined}
                  className="group flex items-center justify-between gap-6 border-t border-[#F7F5F0]/15 py-7 md:py-9"
                >
                  <span className="flex items-center gap-5 md:gap-8 min-w-0">
                    <c.icon size={22} className="shrink-0 text-[#C42021]" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.26em] text-[#B8B8B8]">
                        {pick(content.contact.labels[c.key])}
                      </span>
                      <span className="block truncate font-display text-2xl md:text-4xl lg:text-5xl tracking-[-0.02em] transition-colors duration-300 group-hover:text-[#C42021]">
                        {c.value}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight
                    size={28}
                    className="shrink-0 text-[#F7F5F0]/40 transition-all duration-300 group-hover:text-[#C42021] group-hover:translate-x-1 group-hover:-translate-y-1"
                    aria-hidden="true"
                  />
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.28} y={30}>
              <div className="flex items-center gap-5 md:gap-8 border-t border-b border-[#F7F5F0]/15 py-7 md:py-9">
                <MapPin size={22} className="shrink-0 text-[#C42021]" aria-hidden="true" />
                <span>
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.26em] text-[#B8B8B8]">
                    {pick(content.contact.labels.office)}
                  </span>
                  <span data-testid="contact-office-address" className="block font-display text-xl md:text-3xl tracking-[-0.02em]">
                    {pick(contact.office)}
                  </span>
                </span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-between gap-10">
            <Reveal delay={0.15}>
              <div className="border border-[#F7F5F0]/15 p-8 md:p-10">
                <p className="font-display text-2xl md:text-3xl leading-snug">
                  {pick({ en: "This mission runs on people like you.", hi: "यह मिशन आप जैसे लोगों से चलता है।" })}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#B8B8B8]">{pick(content.contact.volunteerNote)}</p>
                <a
                  href={`${contact.whatsapp}?text=${waText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="volunteer-join-btn"
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#C42021] px-7 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#F7F5F0] transition-colors duration-300 hover:bg-[#F7F5F0] hover:text-[#171717]"
                >
                  {pick(content.contact.volunteer)}
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="flex items-center gap-3" data-testid="contact-social-links">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "")}-link`}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/20 text-[#F7F5F0]/80 transition-colors duration-300 hover:border-[#C42021] hover:bg-[#C42021] hover:text-[#F7F5F0]"
                  >
                    <s.icon size={18} />
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
